import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Stack,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  TextField,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const roleCardSx = {
  p: 1.75,
  borderRadius: 3.5,
  border: '1px solid rgba(17,24,39,0.08)',
  background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)',
  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
};

const sectionTitleSx = {
  fontWeight: 800,
  letterSpacing: 0.2,
};

const quickActionSx = {
  justifyContent: 'center',
  minWidth: 0,
  px: 1.25,
  py: 0.6,
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: 999,
  border: '1px solid rgba(15,23,42,0.08)',
  backgroundColor: 'rgba(255,255,255,0.82)',
};

const SECTION_SCROLL_OFFSET = 40;

function scrollToHelpSection(sectionId, container) {
  const section = document.getElementById(sectionId);
  if (!container || !section) return;
  const top = Math.max(section.offsetTop - SECTION_SCROLL_OFFSET, 0);
  container.scrollTo({ top, behavior: 'smooth' });
}

function BulletList({ items, sx }) {
  return (
    <Stack spacing={0.75} sx={{ mt: 1.25, ...sx }}>
      {items.map((item) => (
        <Typography key={item} variant="body2" color="text.secondary">
          • {item}
        </Typography>
      ))}
    </Stack>
  );
}

function TopicLink({ label, target, contentRef }) {
  return (
    <Button
      onClick={() => scrollToHelpSection(target, contentRef.current)}
      sx={{
        textTransform: 'none',
        px: 1,
        py: 0,
        minWidth: 0,
        fontWeight: 700,
        borderRadius: 999,
      }}
    >
      {label}
    </Button>
  );
}

function HelpSection({ id, chip, title, description, children }) {
  return (
    <Box id={id} sx={{ ...roleCardSx, scrollMarginTop: `${SECTION_SCROLL_OFFSET}px` }}>
      {chip ? <Chip label={chip} size="small" sx={{ mb: 1, fontWeight: 700 }} /> : null}
      <Typography variant="subtitle1" sx={sectionTitleSx}>
        {title}
      </Typography>
      {description ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
          {description}
        </Typography>
      ) : null}
      {children}
    </Box>
  );
}

function Subheading({ children }) {
  return (
    <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 800, color: '#111827' }}>
      {children}
    </Typography>
  );
}

const faqGroups = [
  {
    title: 'Accounts and Setup',
    items: [
      ['What do I need to create an account?', 'Registration asks for first name, last name, username, email, password, role selection, and optionally a profile image. Your selected role determines which setup page you are sent to next.'],
      ['What happens on complete signup?', 'If you arrived through an invite or tokenized flow, complete signup asks for a new password and a confirmation password, validates the signup token, and then signs you into the correct role-based area.'],
      ['What is the difference between reset password and change password?', 'Reset password starts from your email address when you cannot sign in. Change password is for signed-in users and requires your old password plus a new password.'],
    ],
  },
  {
    title: 'Buyer Flows',
    items: [
      ['How do I buy an artwork?', 'Open an artwork detail view, continue into checkout when the work is available, and then confirm the order. After payment succeeds, the order summary and receipt links appear in checkout success and in purchases.'],
      ['What can I do from My Purchases?', 'You can review artwork cards, open receipts, inspect payment status, open the artwork again, and request a refund when the payment is still refundable.'],
      ['Why is my refund waiting?', 'Refunds move through a seller-review step first. The artist or host must approve or reject before payment processing updates the status again.'],
    ],
  },
  {
    title: 'Hosting and Agreements',
    items: [
      ['How do host requests work?', 'Hosts can propose offers or requests, and artists can accept, reject, counter, or later review the agreement in the hosted-artwork management area.'],
      ['What changes for gallery hosts?', 'Gallery hosts unlock a representation workflow in addition to normal hosting. That includes artist management, represented artworks, and linking or creating artworks for represented artists.'],
      ['What do the request statuses mean?', 'Pending means a request still needs a decision, accepted means it is active, rejected and expired mean it will not proceed, cancelled means the agreement was ended, and sold tracks the agreement after a sale is recorded.'],
    ],
  },
];

const contextualReferenceGroups = [
  {
    title: 'Global and Entry Points',
    items: [
      'The navbar Help link opens this drawer from anywhere in the app.',
      'Dashboard question-mark buttons should continue to open the relevant role section.',
      'Login, register, password, and signup-completion pages should point into Getting Started.',
    ],
  },
  {
    title: 'Buyer and Transaction Surfaces',
    items: [
      'Buyer Dashboard should point to Buyer Help.',
      'My Purchases should point to Agreements, Checkout, and Payments.',
      'Checkout success should point to the payments section when users need follow-up context.',
    ],
  },
  {
    title: 'Artist, Host, and Admin Surfaces',
    items: [
      'Artist Dashboard should point to Artist Help.',
      'Host Dashboard should point to Host Help.',
      'Notifications should point to Messaging and Notifications.',
      'Admin pages should point to Admin and Platform Operations.',
    ],
  },
];

const searchableSections = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    keywords: [
      'register',
      'create account',
      'sign in',
      'login',
      'complete signup',
      'reset password',
      'change password',
      'profile setup',
      'buyer setup',
      'artist setup',
      'host setup',
    ],
  },
  {
    id: 'discovering-art',
    title: 'Discovering Art',
    keywords: [
      'discover',
      'nearby art',
      'nearby artworks',
      'nearby artists',
      'nearby hosts',
      'artwork detail',
      'map',
      'radius',
      'favorites',
    ],
  },
  {
    id: 'buyer-help',
    title: 'Buyer Help',
    keywords: [
      'buyer',
      'buyer dashboard',
      'my profile',
      'my purchases',
      'refund',
      'refund request',
      'favorites',
      'buyer setup',
      'purchases',
    ],
  },
  {
    id: 'artist-help',
    title: 'Artist Help',
    keywords: [
      'artist',
      'artist dashboard',
      'my artworks',
      'upload new artwork',
      'manage hosted artworks',
      'sales',
      'payouts',
      'artist setup',
    ],
  },
  {
    id: 'host-help',
    title: 'Host Help',
    keywords: [
      'host',
      'host dashboard',
      'hosted artworks',
      'representation',
      'gallery host',
      'venue',
      'manage hosted artworks',
      'sales',
      'payouts',
      'host setup',
    ],
  },
  {
    id: 'payments-help',
    title: 'Agreements, Checkout, and Payments',
    keywords: [
      'checkout',
      'checkout success',
      'payment',
      'payments',
      'receipt',
      'bill of sale',
      'refund status',
      'agreement',
      'agreements',
    ],
  },
  {
    id: 'messaging-help',
    title: 'Messaging and Notifications',
    keywords: [
      'messages',
      'messaging',
      'chat',
      'conversation',
      'notification',
      'notifications',
      'message button',
    ],
  },
  {
    id: 'admin-help',
    title: 'Admin and Platform Operations',
    keywords: [
      'admin',
      'manager',
      'artist manager',
      'host manager',
      'buyer manager',
      'artwork manager',
      'platform operations',
    ],
  },
  {
    id: 'faq-help',
    title: 'FAQ',
    keywords: ['faq', 'question', 'common questions', 'help topics'],
  },
];

export default function HelpDrawer({ open, onClose, sectionId }) {
  const contentRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');

  const normalizedSearch = searchTerm.trim().toLowerCase();

  const matchingSections = useMemo(() => {
    if (!normalizedSearch) return [];
    return searchableSections.filter(({ title, keywords }) =>
      [title, ...keywords].some((value) => value.toLowerCase().includes(normalizedSearch))
    );
  }, [normalizedSearch]);

  const matchingFaqs = useMemo(() => {
    if (!normalizedSearch) return [];
    return faqGroups.flatMap((group) =>
      group.items
        .filter(([question, answer]) =>
          `${group.title} ${question} ${answer}`.toLowerCase().includes(normalizedSearch)
        )
        .map(([question]) => ({ groupTitle: group.title, question }))
    );
  }, [normalizedSearch]);

  useEffect(() => {
    if (!open || !sectionId) return;

    const timer = window.setTimeout(() => {
      scrollToHelpSection(sectionId, contentRef.current);
    }, 80);

    return () => window.clearTimeout(timer);
  }, [open, sectionId]);

  useEffect(() => {
    if (!open) return;
    setSearchTerm('');
  }, [open, sectionId]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          top: 'var(--navbar-height)',
          height: 'calc(100% - var(--navbar-height))',
          width: { xs: '100%', sm: 480, md: 560 },
          maxWidth: '100vw',
          background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
        },
      }}
    >
      <Box ref={contentRef} sx={{ height: '100%', overflowY: 'auto', p: 2 }}>
        <Box
          sx={{
            p: 2,
            pb: 1.5,
            mb: 2.5,
            borderRadius: 3.5,
            border: '1px solid rgba(17,24,39,0.08)',
            background: 'linear-gradient(180deg, rgba(15,23,42,0.96) 0%, rgba(30,41,59,0.92) 100%)',
            color: '#fff',
            boxShadow: '0 10px 30px rgba(15, 23, 42, 0.18)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
            <Box>
              <Typography variant="overline" sx={{ letterSpacing: 1.2, opacity: 0.82 }}>
                ARTflow Support
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                Help Center
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.75, color: 'rgba(255,255,255,0.82)' }}>
                Guides for discovering art, managing profiles, hosting artwork, purchasing securely, and handling agreements, checkout, and refunds.
              </Typography>
              {sectionId ? (
                <Chip
                  label={`Focused topic: ${sectionId.replace('-help', '').replace('-', ' ')}`}
                  size="small"
                  sx={{
                    mt: 1.25,
                    color: '#fff',
                    backgroundColor: 'rgba(255,255,255,0.14)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    textTransform: 'capitalize',
                  }}
                />
              ) : null}
            </Box>
            <IconButton onClick={onClose} sx={{ color: '#fff' }} aria-label="Close help">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <Stack spacing={2.5}>
            <Box sx={roleCardSx}>
              <Typography variant="subtitle1" sx={{ ...sectionTitleSx, mb: 0.75 }}>
                Search Help
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Search help topics, refunds, hosting, purchases, or agreements"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
              {normalizedSearch ? (
                <Box sx={{ mt: 1.25 }}>
                  {matchingSections.length || matchingFaqs.length ? (
                    <Stack spacing={1}>
                      {matchingSections.length ? (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                            Matching sections
                          </Typography>
                          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            {matchingSections.map((section) => (
                              <Button
                                key={section.id}
                                onClick={() => scrollToHelpSection(section.id, contentRef.current)}
                                sx={quickActionSx}
                              >
                                {section.title}
                              </Button>
                            ))}
                          </Stack>
                        </Box>
                      ) : null}
                      {matchingFaqs.length ? (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                            Matching FAQ entries
                          </Typography>
                          <Stack spacing={0.5}>
                            {matchingFaqs.slice(0, 5).map((item) => (
                              <Typography key={`${item.groupTitle}-${item.question}`} variant="body2" color="text.secondary">
                                • {item.groupTitle}: {item.question}
                              </Typography>
                            ))}
                            <Button
                              onClick={() => scrollToHelpSection('faq-help', contentRef.current)}
                              sx={{ ...quickActionSx, alignSelf: 'flex-start', mt: 0.5 }}
                            >
                              Open FAQ section
                            </Button>
                          </Stack>
                        </Box>
                      ) : null}
                    </Stack>
                  ) : (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      No matching help topics yet. Try terms like buyer, refund, host, checkout, or notifications.
                    </Typography>
                  )}
                </Box>
              ) : (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Search by workflow or keyword, then jump straight to the matching help section.
                </Typography>
              )}
            </Box>

            <Box sx={roleCardSx}>
              <Typography variant="subtitle1" sx={{ ...sectionTitleSx, mb: 0.75 }}>
                Quick Start
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
                Jump to the role or workflow guide that matches what you are doing right now.
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {[
                  ['getting-started', 'Getting started'],
                  ['discovering-art', 'Discovering art'],
                  ['buyer-help', 'Buyer'],
                  ['artist-help', 'Artist'],
                  ['host-help', 'Host'],
                  ['payments-help', 'Payments'],
                  ['messaging-help', 'Messages'],
                  ['admin-help', 'Admin'],
                  ['faq-help', 'FAQ'],
                ].map(([id, label]) => (
                  <Button
                    key={id}
                    onClick={() => scrollToHelpSection(id, contentRef.current)}
                    sx={quickActionSx}
                  >
                    {label}
                  </Button>
                ))}
              </Stack>
            </Box>

            <HelpSection
              id="getting-started"
              title="Getting Started"
              description="This section explains the account-entry forms and how users move from account creation into the correct workflow."
            >
              <Subheading>Create Account / Register</Subheading>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                The registration form asks for first name, last name, username, email, password, role, and an optional profile image. The selected role decides which setup page opens next after registration.
              </Typography>

              <Subheading>Sign In</Subheading>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                The login page asks only for username and password. After successful login, the app routes buyers to the buyer dashboard, artists to the artist dashboard, hosts to the host dashboard, and admins to admin pages.
              </Typography>

              <Subheading>Complete Signup</Subheading>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                The signup completion flow is for tokenized or invited users. It requires a valid signup link plus a new password and confirmation password before it signs the user in.
              </Typography>

              <Subheading>Password Help</Subheading>
              <BulletList
                items={[
                  'Reset Password asks for an email address and sends a reset link if the account exists.',
                  'Change Password is for signed-in users and requires old password plus new password.',
                ]}
              />

              <Subheading>Profile Setup Paths</Subheading>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                After account creation, users should continue into the setup flow for their role. For the detailed fields and what they mean, jump to
                <TopicLink label="Buyer Help" target="buyer-help" contentRef={contentRef} />,
                <TopicLink label="Artist Help" target="artist-help" contentRef={contentRef} />,
                or
                <TopicLink label="Host Help" target="host-help" contentRef={contentRef} />.
              </Typography>
            </HelpSection>

            <HelpSection
              id="discovering-art"
              title="Discovering Art"
              description="These are the core browsing and evaluation flows used by buyers and hosts."
            >
              <Subheading>Home and Discover</Subheading>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                The home and discover views surface featured and nearby art. Users can move from overview content into artwork cards, artist profiles, host profiles, and map-based discovery.
              </Typography>

              <Subheading>Artwork Details</Subheading>
              <BulletList
                items={[
                  'Artwork details provide images, price, status, and engagement context.',
                  'Statuses include available, hosted, sold, and featured.',
                  'Engagement actions include comments, ratings, and favorites where supported.',
                ]}
              />

              <Subheading>Nearby Discovery</Subheading>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                Nearby discovery relies on location and radius controls. Depending on the dashboard, users can browse nearby artworks, nearby hosts, nearby artists, and map markers with quick actions.
              </Typography>
            </HelpSection>

            <HelpSection
              id="buyer-help"
              chip="Buyer"
              title="Buyer Help"
              description="Buyer flows start with setup, continue through discovery, and end in purchases, receipts, and refund follow-up."
            >
              <Subheading>Buyer Profile Setup</Subheading>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                Buyer setup asks for an address and art preferences. The address is geocoded into map coordinates, and the buyer membership agreement may need to be accepted before the profile can be saved.
              </Typography>

              <Subheading>Buyer Dashboard Tabs</Subheading>
              <BulletList
                items={[
                  'My Profile shows the buyer profile card and profile details.',
                  'Nearby Art contains the map, search radius slider, and three sub-tabs: Nearby Artworks, Nearby Hosts, and Nearby Artists.',
                  'My Purchases shows purchased artworks, receipts, payment status, refund controls, and artwork re-entry points.',
                  'My Favorites shows favorites grouped by artworks, artists, and hosts.',
                ]}
              />

              <Subheading>Purchases and Refunds</Subheading>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                The purchases view loads recent purchases, shows receipt links and payment states, and allows refund requests when the payment is still eligible. Refund requests require a reason and then move through requested, pending, rejected, or refunded states.
              </Typography>
            </HelpSection>

            <HelpSection
              id="artist-help"
              chip="Artist"
              title="Artist Help"
              description="Artist flows combine public-profile management, artwork publishing, hosting agreements, and sales follow-up."
            >
              <Subheading>Artist Profile Setup</Subheading>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                Artist setup asks for about text, exhibitions, recognitions, portfolio link, address, and art preferences. The address is geocoded, and the artist platform agreement may need to be accepted before the profile is created.
              </Typography>

              <Subheading>Artist Dashboard Tabs</Subheading>
              <BulletList
                items={[
                  'My Profile shows and edits the artist profile card.',
                  'My Artworks lists the artist’s artworks with status filtering.',
                  'Nearby Art has a map plus two sub-tabs: Nearby Hosts and Nearby Artists.',
                  'Manage Hosted Artworks has six sub-tabs: Pending, Accepted, Rejected, Expired, Cancelled, and Sold.',
                  'Upload New Artwork opens the artwork creation workflow.',
                  'My Favorites groups favorite artworks, artists, and hosts.',
                  'My Sales shows sales and refund-decision handling.',
                  'My Payouts shows payout-related information.',
                ]}
              />

              <Subheading>Upload and Artwork Management</Subheading>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                Use the upload flow for new works and the My Artworks tab to review status-filtered inventory. For the standalone creation screen, open the artwork-create route directly.
              </Typography>
            </HelpSection>

            <HelpSection
              id="host-help"
              chip="Host"
              title="Host Help"
              description="Host workflows cover venue setup, nearby discovery, hosting requests, hosted artworks, and gallery-only representation tools."
            >
              <Subheading>Host Profile Setup</Subheading>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                Host setup asks for address, venue type, venue description, display space details, lighting and environment options, past art events, art preferences, curation philosophy, staffing, foot traffic estimate, display availability, hosting motivation, and hosting outcome goals. The selected venue type also determines whether the host membership agreement or gallery membership agreement is required.
              </Typography>

              <Subheading>Host Dashboard Tabs</Subheading>
              <BulletList
                items={[
                  'My Profile shows the host profile card and venue presentation.',
                  'My Hosted ARTflow Artworks lists hosted works with status filters.',
                  'My Art Representations appears only for gallery hosts and manages represented artists and represented artworks.',
                  'Nearby Art has a map plus three sub-tabs: Nearby Artworks, Nearby Hosts, and Nearby Artists.',
                  'Manage Hosted Artworks has six sub-tabs: Pending, Accepted, Rejected, Expired, Cancelled, and Sold.',
                  'My Favorites groups favorites across artworks, artists, and hosts.',
                  'My Sales shows host-side sales and refund decision actions.',
                  'My Payouts shows payout information.',
                ]}
              />

              <Subheading>Gallery Host Representation Flow</Subheading>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                Gallery hosts can add new artists, link existing artists, inspect a selected artist profile, and then manage two artwork lanes: works already represented by the gallery and works eligible for gallery representation.
              </Typography>
            </HelpSection>

            <HelpSection
              id="payments-help"
              chip="Payments"
              title="Agreements, Checkout, and Payments"
              description="This section explains the higher-friction transactional steps and how payment-related records show up later."
            >
              <Subheading>Required Agreements</Subheading>
              <BulletList
                items={[
                  'Buyer setup may require the buyer membership agreement.',
                  'Artist setup may require the artist platform agreement.',
                  'Host setup may require host membership or gallery membership depending on venue type.',
                  'Checkout can also surface bill-of-sale acceptance requirements.',
                ]}
              />

              <Subheading>Checkout Success</Subheading>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                The checkout success page confirms payment, loads an order summary by session id, shows artwork title, price, payment status, and may expose links to the Stripe receipt and bill of sale. It also points buyers back to My Purchases for later follow-up.
              </Typography>

              <Subheading>Payments and Refund Statuses</Subheading>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                Payment details reappear in buyer purchases and sales-report views. Refunds begin with a buyer request, then move to seller approval or rejection, and then update again once Stripe processing completes.
              </Typography>
            </HelpSection>

            <HelpSection
              id="messaging-help"
              chip="Messages"
              title="Messaging and Notifications"
              description="Messages and notifications help users continue conversations and follow workflow changes across the platform."
            >
              <Subheading>Messages</Subheading>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                The messages drawer lists conversations, allows new conversations by searching users, opens a chat modal for the selected thread, and supports sending new messages. Logged-in users can also launch conversations from profile cards and nearby map marker popups.
              </Typography>

              <Subheading>Notifications</Subheading>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                The notifications page lists unread and read notifications, displays how long ago they were created, and allows users to mark individual notifications as read. Offer, agreement, refund, and other workflow events can surface here.
              </Typography>
            </HelpSection>

            <HelpSection
              id="admin-help"
              chip="Admin"
              title="Admin and Platform Operations"
              description="Admin tools stay separate from role-specific help so buyer, artist, and host guidance stays focused."
            >
              <Subheading>Admin Areas</Subheading>
              <BulletList
                items={[
                  'The main admin page acts as the operational hub.',
                  'Dedicated admin pages exist for artists, hosts, buyers, and artworks.',
                  'These areas are for review and platform management rather than end-user workflow steps.',
                ]}
              />
            </HelpSection>

            <Box id="faq-help" sx={{ ...roleCardSx, scrollMarginTop: `${SECTION_SCROLL_OFFSET}px` }}>
              <Typography variant="subtitle1" sx={{ ...sectionTitleSx, mb: 0.75 }}>
                FAQ
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
                Common questions grouped by workflow area.
              </Typography>
              {faqGroups.map((group) => (
                <Box key={group.title} sx={{ mb: 1.25 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {group.title}
                  </Typography>
                  {group.items.map(([question, answer]) => (
                    <Accordion
                      key={question}
                      disableGutters
                      elevation={0}
                      sx={{ background: 'transparent', '&:before': { display: 'none' } }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography sx={{ fontWeight: 700 }}>{question}</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography variant="body2" color="text.secondary">
                          {answer}
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              ))}
            </Box>

            <Box id="contextual-help-references" sx={{ ...roleCardSx, scrollMarginTop: `${SECTION_SCROLL_OFFSET}px` }}>
              <Typography variant="subtitle1" sx={{ ...sectionTitleSx, mb: 0.75 }}>
                Contextual Help References
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
                These are the places in the product that should keep pointing back to the right help section.
              </Typography>
              <Stack spacing={1.5}>
                {contextualReferenceGroups.map((group) => (
                  <Box key={group.title}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {group.title}
                    </Typography>
                    <BulletList items={group.items} />
                  </Box>
                ))}
              </Stack>
            </Box>

            <Box sx={roleCardSx}>
              <Typography variant="subtitle1" sx={{ ...sectionTitleSx, mb: 0.75 }}>
                Need More Help
              </Typography>
              <Typography variant="body2" color="text.secondary">
                If you are already inside a workflow and need to act on something, notifications and messages are usually the fastest next step. If you just need orientation, use the section jumps above to return to the right guide.
              </Typography>
              <Divider sx={{ my: 1.5 }} />
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Button onClick={() => scrollToHelpSection('getting-started', contentRef.current)} sx={quickActionSx}>
                  Back to getting started
                </Button>
                <Button onClick={() => scrollToHelpSection('payments-help', contentRef.current)} sx={quickActionSx}>
                  Jump to payments
                </Button>
              </Stack>
            </Box>
        </Stack>
      </Box>
    </Drawer>
  );
}
